import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";

import withRouter from "../../../components/Common/withRouter";
import GlobalLoader from "../../../components/Common/GlobalLoader";
import TableContainerWarehouseRx from "../../../components/Common/TableContainerWarehouseRx";

import {
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import { getLoansAndAdvancedSalary } from "../../../slice/thunks";

const GetLoansAndAdvancedSalary = (props) => {
  const dispatch = useDispatch();
  const history = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize] = useState(10);

  const selectLayoutState = (state) => state.Settings;
  const OutputsProperties = createSelector(selectLayoutState, (outputs) => ({
    loansAndAdvancedSalary: outputs.loansAndAdvancedSalary,
    error: outputs.error,
  }));

  const { loansAndAdvancedSalary, error } = useSelector(OutputsProperties);

  const items = loansAndAdvancedSalary?.items || [];
  const tableTotal = loansAndAdvancedSalary?.total_items || items.length;
  const tableTotalPage = loansAndAdvancedSalary?.total_page || 1;

  const loadLoans = useCallback(
    (page = 1, term = "") => {
      setIsLoading(true);
      dispatch(getLoansAndAdvancedSalary({ page, size: pageSize, term }))
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
      setCurrentPage(page);
      setSearchTerm(term);
    },
    [dispatch, pageSize]
  );

  useEffect(() => {
    loadLoans(currentPage, searchTerm);
  }, [loadLoans, currentPage, searchTerm]);

  const columns = useMemo(
    () => [
      {
        header: props.t("employee_name"),
        accessorKey: "fullName",
        enableColumnFilter: false,
      },
      {
        header: props.t("salary"),
        accessorKey: "salary",
        cell: (info) => info.getValue().toLocaleString(),
        enableColumnFilter: false,
      },
      {
        header: props.t("balance"),
        accessorKey: "amountBalance",
        cell: (info) => info.getValue().toLocaleString(),
        enableColumnFilter: false,
      },
      {
        header: props.t("monthly_payment"),
        accessorKey: "amountPerMonth",
        cell: (info) => info.getValue().toLocaleString(),
        enableColumnFilter: false,
      },
      {
        header: props.t("remaining_months"),
        accessorKey: "numberMonthBalance",
        enableColumnFilter: false,
      },
      {
        header: props.t("owned_amount"),
        accessorKey: "ownedAmount",
        cell: (info) => info.getValue().toLocaleString(),
        enableColumnFilter: false,
      },
      {
        header: props.t("Actions"),
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <UncontrolledDropdown>
            <DropdownToggle
              href="#"
              className="btn btn-soft-secondary btn-sm"
              tag="button"
            >
              <i className="fa-solid fa-ellipsis"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem
                onClick={() => {
                  const nameSlug = encodeURIComponent(row.original.fullName);
                  history(
                    `/payroll/loans/${row.original.employeeId}/assign/${nameSlug}`
                  );
                }}
              >
                {props.t("assign_loan")}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                onClick={() => {
                  const nameSlug = encodeURIComponent(row.original.fullName);
                  history(
                    `/payroll/advanced-salary/${row.original.employeeId}/assign/${nameSlug}`
                  );
                }}
              >
                {props.t("assign_advanced_salary")}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    [props.t, history]
  );

  return (
    <div className="patients-list-content">
      <div className="patients-list px-2 pb-5">
        <div className="container-fluid">
          <div className="heading-parent-sec">
            <div className="heading-child-one-sec pt-4">
              <h6>{props.t("loan_and_advanced_salary_management")}</h6>
            </div>
          </div>

          <div className="card border-0 row p-2">
            {isLoading ? (
              <GlobalLoader />
            ) : error ? (
              <div className="alert alert-danger mt-2">{String(error)}</div>
            ) : (
              <TableContainerWarehouseRx
                columns={columns}
                data={items}
                tablePage={currentPage}
                tableTotal={tableTotal}
                tableTotalPage={tableTotalPage}
                onNextPage={(page) => loadLoans(page, searchTerm)}
                isGlobalFilter={true}
                onSearch={(term) => loadLoans(1, term)}
                SearchPlaceholder={props.t("search_employee")}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(withTranslation()(GetLoansAndAdvancedSalary));
